import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { dayOf, describe } from '../../dates';
import { useAnswerSaves } from '../../contexts/answer-saves';
import { useLanguage } from '../../contexts/language';
import { useAttemptChanges } from '../../hooks/attempt-changes';
import { useVisit } from '../../hooks/visit';

import { Button } from '../button';
import { LoadFailed } from '../load-failed';

import styles from './style.module.css';

// The sentence the service refuses an incomplete submission with, checked
// here first so the patient learns it without a round trip.
const incomplete = 'Answer every question before submitting';

// Every item with its answer before submitting, each a link back to its item.
// Every item must be answered (owner, 2026-10-09): the unanswered ones are
// called out, and Submit refuses and moves focus to the first of them.
//
// A submitted attempt shows the same list read-only, as it was sent.
export function TestCheck({ attempt, progress = 'current', onReadProgress }) {
  const { text, language, translate } = useLanguage();
  const navigate = useNavigate();
  const { submit } = useAttemptChanges();
  const { settleAttempt } = useAnswerSaves();
  const capture = useVisit();
  const [ busy, setBusy ] = useState(false);
  const [ error, setError ] = useState();
  const firstMissing = useRef(null);
  const { id, status, assignedBy, submittedAt, questionnaire, answers } = attempt;
  const draft = status === 'DRAFT';

  const missing = useMemo(function() {
    return questionnaire.items.filter(function({ position }) {
      return answers.some(function(answer) {
        return answer.position === position;
      }) === false;
    });
  }, [ questionnaire, answers ]);

  const onSubmit = useCallback(function() {
    // What is missing is only known from the latest read. Submit waits while
    // one is under way, and after a failed one it reads again rather than
    // refuse a complete questionnaire for an answer the screen has not
    // caught up with (#123).
    if (progress === 'failed') {
      setError();

      return onReadProgress();
    }

    if (missing.length > 0) {
      setError(incomplete);
      firstMissing.current.focus();

      return null;
    }

    setBusy(true);

    // An answer still on its way is waited for, and one that failed to save
    // stops the submission with its own message, so what is submitted is
    // what the patient last chose (#114). The submission goes ahead even if
    // the patient leaves meanwhile; only the move to Tests and the error
    // belong to this visit (#135).
    const owns = capture();

    return settleAttempt(id).then(function(failure) {
      if (failure != null) {
        throw new Error(failure);
      }

      return submit(id);
    }).then(function() {
      if (owns() === true) {
        navigate('/tests');
      }
    }).catch(function(failed) {
      if (owns() === false) {
        return;
      }

      setBusy(false);
      setError(failed.message);
    });
  }, [ id, progress, missing, settleAttempt, submit, onReadProgress, navigate, capture ]);

  let progressFailed = null;

  if (progress === 'failed') {
    progressFailed = <LoadFailed title={ text.progressFailed } onRetry={ onReadProgress }/>;
  }

  const rows = useMemo(function() {
    return questionnaire.items.map(function({ position, text: wording }) {
      const answer = answers.find(function(candidate) {
        return candidate.position === position;
      });
      let answered = <span className={ styles.missing }>{ text.notAnswered }</span>;

      if (answer != null) {
        const option = questionnaire.options.find(function({ value }) {
          return value === answer.value;
        });

        answered = <span className={ styles.answer } lang={ questionnaire.language }>{ `${ option.value } · ${ option.label }` }</span>;
      }

      const content = (
        <>
          <span className={ styles.number }>{ position }</span>

          <span className={ styles.item }>
            <span lang={ questionnaire.language }>{ wording }</span>
            { answered }
          </span>
        </>
      );

      if (draft === false) {
        return <div key={ position } className={ styles.row }>{ content }</div>;
      }

      let target = null;

      if (missing.length > 0 && missing[ 0 ].position === position) {
        target = firstMissing;
      }

      return (
        <Link key={ position } ref={ target } className={ styles.row } to={ `/tests/${ id }/${ position }` }>
          { content }

          <span className={ styles.edit }>{ text.edit }</span>
        </Link>
      );
    });
  }, [ questionnaire, answers, draft, missing, id, text ]);

  const subtitle = useMemo(function() {
    const languageName = new Intl.DisplayNames([ language ], { type: 'language' }).of(questionnaire.language);

    return <span className={ styles.subtitle }>{ `${ questionnaire.name } · ${ questionnaire.version } · ${ languageName }` }</span>;
  }, [ questionnaire, language ]);

  // Held before the read-only view returns early, so every render calls the
  // same hooks.
  const notice = useMemo(function() {
    if (missing.length === 1) {
      return <p className={ styles.notice } role='note'>{ text.missingOne }</p>;
    }

    if (missing.length > 1) {
      return <p className={ styles.notice } role='note'>{ text.missingMany.replace('{count}', missing.length) }</p>;
    }

    return null;
  }, [ missing, text ]);

  const message = useMemo(function() {
    if (error == null) {
      return null;
    }

    return <span className={ styles.error } role='alert'>{ translate(error) }</span>;
  }, [ error, translate ]);

  if (draft === false) {
    return (
      <section className={ styles.check }>
        <Link className={ styles.back } to='/tests'>{ text.backToTests }</Link>

        <div className={ styles.heading }>
          <h1 className={ styles.title }>{ text.yourAnswers }</h1>
          { subtitle }
        </div>

        <p className={ styles.kept }>{ text.submittedKept.replace('{date}', describe({ day: dayOf(submittedAt), language, short: true })) }</p>

        <div className={ styles.list }>{ rows }</div>
      </section>
    );
  }

  // Back to questions returns to the first unanswered item, or the last one.
  let resume = questionnaire.items.length;

  if (missing.length > 0) {
    resume = missing[ 0 ].position;
  }

  let recipient = text.yourClinician;
  let submitLabel = text.submit;

  if (assignedBy != null) {
    recipient = assignedBy;
    submitLabel = text.submitTo.replace('{name}', assignedBy);
  }

  return (
    <section className={ styles.check }>
      <Link className={ styles.back } to={ `/tests/${ id }/${ resume }` }>{ text.backToQuestions }</Link>

      <div className={ styles.heading }>
        <h1 className={ styles.title }>{ text.checkAnswers }</h1>
        { subtitle }
      </div>

      { notice }

      { progressFailed }

      <div className={ styles.list }>{ rows }</div>

      <div className={ styles.send }>
        <p>{ text.submitNote.replace('{name}', recipient) }</p>

        <Button disabled={ busy || progress === 'reading' } onClick={ onSubmit }>{ submitLabel }</Button>

        { message }
      </div>
    </section>
  );
}
